import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROTULO_PRESENCA,
  zAjusteDeReposicao,
  type AulaDoAluno,
  type MovimentoDeReposicao,
  type TipoDeMovimentoDeReposicao,
  type TurmasDoAluno,
} from '@arena/shared/schemas';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { turmasApi } from '../api/turmas.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { horaCurta } from '../agenda/formatos.js';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { useDica } from '../ui/Dica.js';
import { Botao, Campo, Escolhas, Mini, Nota, Status, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { dataCompleta, diasCurtos, saidaPorExtenso } from './formatos.js';

/**
 * As abas Turmas e Presenças de `#m-aluno` — vazias desde o documento 08,
 * "até o documento 10 chegar".
 *
 * As duas leem a mesma consulta (`/turmas/alunos/:id`): matrículas com
 * período, as últimas aulas, o saldo de reposição e os movimentos que o
 * explicam. O servidor responde pelas turmas do ESCOPO de quem abriu — o
 * Professor vê as matrículas e as presenças das turmas dele.
 */

function useTurmasDoAluno(alunoId: string) {
  const { pode } = useSessao();
  return useQuery({
    queryKey: ['turmas', 'aluno', alunoId],
    queryFn: () => turmasApi.doAluno(alunoId),
    enabled: pode('turmas', 'ler'),
    retry: false,
  });
}

function EstadoDaConsulta({
  consulta,
  children,
}: {
  consulta: ReturnType<typeof useTurmasDoAluno>;
  children: (dados: TurmasDoAluno) => React.ReactNode;
}) {
  const { pode } = useSessao();
  if (!pode('turmas', 'ler')) {
    return <Vazio>Seu perfil não acessa turmas.</Vazio>;
  }
  if (consulta.isPending) return <EsqueletoDeCard linhas={3} />;
  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Erro
        tipo={
          erro instanceof ErroDeRede
            ? 'rede'
            : erro instanceof ErroDeApi && erro.ehPermissao
              ? 'permissao'
              : 'falha'
        }
        detalhe={mensagemDoErro(erro)}
        onTentarDeNovo={() => void consulta.refetch()}
      />
    );
  }
  return <>{children(consulta.data)}</>;
}

// ---------------------------------------------------------------------
// Turmas
// ---------------------------------------------------------------------

/**
 * "Futvôlei ter/qui 19h · matriculada em 12/03/2026 — Ativa" e, embaixo,
 * "Futvôlei ter/qui 17h · saiu em 02/06/2026 · transferência — Encerrada":
 * matrícula é registro com período, e as duas linhas ficam.
 */
export function AbaTurmasDoAluno({ alunoId }: { alunoId: string }) {
  const consulta = useTurmasDoAluno(alunoId);
  return (
    <EstadoDaConsulta consulta={consulta}>
      {(dados) =>
        dados.matriculas.length === 0 ? (
          <Vazio>Nenhuma matrícula em turma.</Vazio>
        ) : (
          <>
            <ul className="feed">
              {dados.matriculas.map((m) => (
                <li key={m.id}>
                  <div>
                    <b>
                      {m.turma.titulo}
                      {m.turma.grade
                        ? ` ${diasCurtos(m.turma.grade.diasSemana)} ${horaCurta(m.turma.grade.horaInicio)}`
                        : ''}
                    </b>
                    <small>
                      {m.ativa
                        ? `matrícula em ${dataCompleta(m.entrouEm)} · Prof. ${m.turma.professor}`
                        : saidaPorExtenso(m.saiuEm as string, m.motivoSaida)}
                    </small>
                  </div>
                  <div className="rt">
                    <Status situacao={m.ativa ? 'ok' : 'neutra'}>
                      {m.ativa ? 'Ativa' : 'Encerrada'}
                    </Status>
                  </div>
                </li>
              ))}
            </ul>
            {dados.saldoDeReposicao > 0 && (
              <p className="sub">
                {dados.saldoDeReposicao === 1
                  ? '1 reposição disponível.'
                  : `${dados.saldoDeReposicao} reposições disponíveis.`}
              </p>
            )}
          </>
        )
      }
    </EstadoDaConsulta>
  );
}

// ---------------------------------------------------------------------
// Presenças
// ---------------------------------------------------------------------

const COR_DA_AULA = (aula: AulaDoAluno): string => {
  if (aula.estado === 'Presente') return 'var(--green)';
  if (aula.estado === 'Reposicao') return 'var(--blue)';
  return aula.justificada ? 'var(--amber)' : 'var(--red)';
};

const ROTULO_DA_ORIGEM: Readonly<Record<MovimentoDeReposicao['origem'], string>> = {
  FaltaJustificada: 'falta justificada',
  UsoEmAula: 'reposição usada',
  Estorno: 'estorno',
  Ajuste: 'ajuste manual',
};

export function AbaPresencasDoAluno({ alunoId }: { alunoId: string }) {
  const consulta = useTurmasDoAluno(alunoId);
  const dica = useDica();

  return (
    <EstadoDaConsulta consulta={consulta}>
      {(dados) => (
        <>
          <Mini
            itens={[
              {
                valor: dados.presencaNoSemestre === null ? '—' : `${dados.presencaNoSemestre}%`,
                rotulo: 'presença no semestre',
              },
              { valor: dados.faltasSeguidas, rotulo: 'faltas seguidas' },
              { valor: dados.saldoDeReposicao, rotulo: 'reposições disponíveis' },
            ]}
          />

          <div>
            <div className="card-head" style={{ marginBottom: 10 }}>
              <h2>Últimas aulas</h2>
              <span className="hint">passe o mouse nas barras</span>
            </div>
            {dados.ultimasAulas.length === 0 ? (
              <Vazio>Nenhuma chamada registrada ainda.</Vazio>
            ) : (
              <div className="aulas-do-aluno">
                {dados.ultimasAulas.map((aula) => (
                  <div key={aula.marcacaoId}>
                    <i
                      style={{ background: COR_DA_AULA(aula) }}
                      onMouseEnter={(e) =>
                        dica.mostrar(e, {
                          titulo: `${aula.data.slice(8, 10)}/${aula.data.slice(5, 7)}`,
                          texto: `${aula.justificada ? 'Falta justificada' : ROTULO_PRESENCA[aula.estado]} · ${aula.turma}`,
                        })
                      }
                      onMouseMove={(e) => dica.mover(e)}
                      onMouseLeave={() => dica.esconder()}
                    />
                    {aula.data.slice(8, 10)}/{aula.data.slice(5, 7)}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="card-head" style={{ marginBottom: 10 }}>
              <h2>Reposições</h2>
              <span className="hint">cada crédito e débito, com a origem</span>
            </div>
            {dados.movimentos.length === 0 ? (
              <Vazio>Nenhum movimento de reposição.</Vazio>
            ) : (
              <ul className="feed">
                {dados.movimentos.map((m) => (
                  <li key={m.id}>
                    <div>
                      <b>
                        {m.tipo === 'Credito' ? 'Crédito' : 'Débito'} · {ROTULO_DA_ORIGEM[m.origem]}
                        {m.estornado ? ' · estornado' : ''}
                      </b>
                      <small>
                        {m.dataDaAula ? `aula de ${dataCompleta(m.dataDaAula)}` : 'sem aula'}
                        {m.motivo ? ` · ${m.motivo}` : ''}
                      </small>
                    </div>
                    <div className="rt">
                      <b>{m.tipo === 'Credito' ? '+1' : '−1'}</b>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <AjusteDeReposicao alunoId={alunoId} />
        </>
      )}
    </EstadoDaConsulta>
  );
}

/**
 * O ajuste manual — Dono e Gestão. A política de reposição (quantas por
 * período, validade) é pergunta aberta do documento 10; enquanto ela não
 * tem resposta, o ajuste com MOTIVO cobre o que a regra automática não
 * cobre, e fica no extrato acima.
 */
function AjusteDeReposicao({ alunoId }: { alunoId: string }) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [tipo, setTipo] = useState<TipoDeMovimentoDeReposicao>('Credito');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const ajustar = useMutation({
    mutationFn: () =>
      turmasApi.ajustarReposicao(zAjusteDeReposicao.parse({ alunoId, tipo, motivo })),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['turmas', 'aluno', alunoId] }),
  });

  if (!pode('turmas', 'editar')) return null;
  if (!aberto) {
    return (
      <Botao
        variante="contorno"
        pequeno
        onClick={() => setAberto(true)}
        style={{ alignSelf: 'flex-start' }}
      >
        Ajustar saldo de reposição
      </Botao>
    );
  }

  async function salvar() {
    setErro(null);
    const pedido = zAjusteDeReposicao.safeParse({ alunoId, tipo, motivo });
    if (!pedido.success) {
      setErro(pedido.error.issues[0]?.message ?? 'Confira o ajuste.');
      return;
    }
    try {
      const { saldo } = await ajustar.mutateAsync();
      avisar(
        `Saldo ajustado. ${saldo === 1 ? '1 reposição disponível' : `${saldo} reposições disponíveis`}.`,
      );
      setAberto(false);
      setMotivo('');
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Nota>
        A regra de reposição — quantas por período, validade, o que justifica uma falta — ainda é
        decisão do dono. O ajuste manual registra o motivo e aparece no extrato.
      </Nota>
      <Escolhas
        aria-label="Tipo de ajuste"
        opcoes={[
          { valor: 'Credito', rotulo: 'Crédito (+1)' },
          { valor: 'Debito', rotulo: 'Débito (−1)' },
        ]}
        valor={tipo}
        onChange={setTipo}
      />
      <Campo rotulo="Motivo" erro={erro ?? undefined}>
        <input
          value={motivo}
          placeholder="Atestado médico, cortesia, correção…"
          onChange={(e) => setMotivo(e.target.value)}
        />
      </Campo>
      <div style={{ display: 'flex', gap: 8 }}>
        <Botao variante="fantasma" pequeno onClick={() => setAberto(false)}>
          Cancelar
        </Botao>
        <Botao pequeno onClick={() => void salvar()} disabled={ajustar.isPending}>
          {ajustar.isPending ? 'Salvando…' : 'Salvar ajuste'}
        </Botao>
      </div>
    </div>
  );
}
